import {animate, AnimationOptions, inView} from 'motion';
import ButtonDownloadHandler from './download-button-handler';

import styles from '../../styles/tailwind.pcss'
import {DynamicComponentsInitEvent} from '../../types';

export default class AtelierLayout extends HTMLElement {
    declare shadowRoot: ShadowRoot;

    constructor() {
        super();
        this.attachShadow({ mode: 'open' })
    }

    connectedCallback() {
        this.render();

        this.setupTransitions();
        this.setupMarquee();
        this.setupDownloadButtons();
        this.setupFormPopupButtons();
        this.setupFullscreenVideo();
        this.setupVideoAutoplay();

        this.shadowRoot.querySelectorAll('a[href^="#"]').forEach( (link) => {
            link.addEventListener("click", this.sectionNavClick.bind(this));
        });

        window.addEventListener('dynamicComponentsInit', ((e: DynamicComponentsInitEvent) => {
            const {components} = e.detail

            if (!components.loggedIn) return

            components.loggedIn.modifyModalLinks();
        }) as EventListener)
    }

    private render() {
        const content = this.innerHTML;

        const styleSheet = new CSSStyleSheet();
        styleSheet.replaceSync(styles)

        const rootStyleSheet = new CSSStyleSheet();

        for (const rule of Array.from(styleSheet.cssRules)) {
            if (rule instanceof CSSPropertyRule) {
                rootStyleSheet.insertRule(rule.cssText)
            }
        }
        document.adoptedStyleSheets.push(rootStyleSheet);

        this.shadowRoot.adoptedStyleSheets = [styleSheet];
        this.shadowRoot.innerHTML = content;
        this.innerHTML = '';
    }

    private sectionNavClick(e: Event) {
        const target = e.target as HTMLElement;

        this.shadowRoot.querySelector(target.getAttribute('href') as string)?.scrollIntoView({
            behavior: 'smooth'
        });
    }

    private isMobileAtelier(): boolean {
        return window.matchMedia('(max-width: 767px)').matches;
    }

    private setupTransitions() {
        const elements: NodeListOf<HTMLElement> = this.shadowRoot.querySelectorAll('[data-parallax]')
        const duration = this.isMobileAtelier() ? 0.45 : 0.65;

        elements.forEach((element) => {
            element.style.opacity = '0';
            element.style.transform = 'translateY(160px)';
        });

        inView(elements, (element) => {
            animate(element, {
                opacity: [0, 1],
                y: [160, 0],
            }, {
                duration: duration,
                easing: {
                    opacity: 'ease-out',
                    y: 'cubic-bezier(0.16, 1, 0.3, 1)'
                }
            } as AnimationOptions)
        }, {
            margin: '0%'
        });
    }

    private setupMarquee() {
        const duration = 12;
        const marquee: HTMLElement | null = this.shadowRoot.querySelector('[data-marquee]')

        if (!marquee) return

        const clone = marquee.innerHTML;
        marquee.insertAdjacentHTML('beforeend', clone);

        animate(
            Array.from(marquee.children),
            {
                x: ['0%', '-100%']
            },
            {
                duration,
                repeat: Infinity,
                ease: 'linear',
            }
        )
    }

    private setupDownloadButtons() {
        const downloadHandler = new ButtonDownloadHandler();
        downloadHandler.init(this.shadowRoot);
    }



    private setupFormPopupButtons() {
        const buttons = this.shadowRoot.querySelectorAll('[data-toggle="form-popup"]');

        buttons.forEach((button) => {
            button.addEventListener('click', (e: Event) => {
                const target = e.currentTarget as HTMLElement;
                if (target && target.dataset.config) {
                    const targetConfig = JSON.parse(target.dataset.config);

                    // Add target and size from data attributes if not in config
                    if (!targetConfig.target && target.dataset.target) {
                        targetConfig.target = target.dataset.target;
                    }
                    if (!targetConfig.size && target.dataset.size) {
                        targetConfig.size = target.dataset.size;
                    }

                    document.dispatchEvent(new CustomEvent('formpopup:open', {
                        detail: targetConfig
                    }));
                }
            });
        });
    }

    private setupFullscreenVideo(){
        const buttons = this.shadowRoot.querySelectorAll('[data-toggle-fullscreen]');

        buttons.forEach((button) => {
            button.addEventListener('click', (e: Event) => {
                const target = e.currentTarget as HTMLElement;
                const video = target.nextElementSibling as HTMLVideoElement;

                if (video && video.tagName === 'VIDEO') {
                    const fullscreenSrc = video.getAttribute('data-fullscreen-src');

                    if (fullscreenSrc) {
                        const fullscreenVideo = document.createElement('video');
                        fullscreenVideo.src = fullscreenSrc;
                        fullscreenVideo.controls = true;
                        fullscreenVideo.playsInline = true;

                        const cleanup = () => {
                            fullscreenVideo.pause();
                            fullscreenVideo.remove();
                        };

                        // iOS Safari Support
                        const fullscreenChangeEvent = 'webkitfullscreenchange' in document
                            ? 'webkitfullscreenchange'
                            : 'fullscreenchange';

                        fullscreenVideo.addEventListener(fullscreenChangeEvent, () => {
                            const isFullscreen = document.fullscreenElement ||
                                               (document as any).webkitFullscreenElement;
                            if (!isFullscreen) {
                                cleanup();
                            }
                        });

                        this.shadowRoot.appendChild(fullscreenVideo);

                        fullscreenVideo.play().then(() => {
                            // iOS Safari: webkitEnterFullscreen
                            if ('webkitEnterFullscreen' in fullscreenVideo) {
                                (fullscreenVideo as any).webkitEnterFullscreen();
                            } else if (fullscreenVideo.requestFullscreen) {
                                fullscreenVideo.requestFullscreen();
                            }
                        }).catch(() => {
                            cleanup();
                        });
                    } else {
                        video.play();
                        // iOS Safari Support
                        if ('webkitEnterFullscreen' in video) {
                            (video as any).webkitEnterFullscreen();
                        } else if (video.requestFullscreen) {
                            video.requestFullscreen();
                        }
                    }
                }
            });
        });
    }

    private setupVideoAutoplay() {
        const videos: NodeListOf<HTMLVideoElement> = this.shadowRoot.querySelectorAll('[data-video]');

        if (videos.length === 0) return;

        // Lazy loading observer - lädt das Video früher
        const lazyLoadObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                //console.log(entry.target, entry.isIntersecting)
                const video = entry.target as HTMLVideoElement;

                if (entry.isIntersecting) {
                    const dataSrc = video.getAttribute('data-src');

                    if (dataSrc) {
                        // Only set opacity to 0 on first load
                        if (!video.dataset.loaded) {
                            video.style.opacity = '0';
                            video.dataset.loaded = 'true';
                        }
                        video.src = dataSrc;
                        video.load();
                    }
                } else {
                    video.src = '';
                    video.load();
                    //console.log('video unloaded', video)
                }
            });
        }, {
            root: null,
            rootMargin: '400px 0px 200px 0px', // More margin on top for smooth upward scrolling
            //threshold: 0.025

        });

        // Autoplay observer - spielt das Video ab wenn sichtbar
        const autoplayObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const video = entry.target as HTMLVideoElement;

                if (entry.isIntersecting) {
                    // Fade in when video starts playing (only first time)
                    if (!video.dataset.faded) {
                        video.dataset.faded = 'true';
                        animate(video as any, {
                            opacity: [0, 1]
                        }, {
                            duration: 0.6,
                            ease: 'easeOut'
                        });
                    }
                    video.play().catch(() => {});
                } else {
                    video.pause();
                }
            });
        }, {
            root: null,
            rootMargin: '0px',
            threshold: 0.075
        });

        videos.forEach((video) => {
            video.addEventListener('loadeddata', () => {
                //console.log('loadeddata', e);
                const boundingRect = video.getBoundingClientRect();
                video.style.aspectRatio = `${boundingRect.width} / ${boundingRect.height}`;
            })
            lazyLoadObserver.observe(video);
            autoplayObserver.observe(video);
        });
    }
}
